import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import {
	$convertFromMarkdownString,
	$convertToMarkdownString,
	TRANSFORMERS
} from '@lexical/markdown';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import he from 'he';
import {
	$createParagraphNode,
	$createTextNode,
	$getRoot,
	$parseSerializedNode,
	createEditor,
	type SerializedEditorState,
	type SerializedLexicalNode,
	type SerializedParagraphNode,
	type SerializedTextNode
} from 'lexical';
import { DiffNode } from '@/components/editor/components/highlight-difference-plugin/diff-node';
import { MentionNode } from '@/components/editor/components/mention/mention-node';

const EDITOR_NODES = [
	DiffNode,
	HeadingNode,
	ListNode,
	ListItemNode,
	QuoteNode,
	CodeNode,
	CodeHighlightNode,
	LinkNode,
	AutoLinkNode,
	MentionNode
];

const createLexicalEditor = () =>
	createEditor({
		namespace: 'editor-conversion',
		nodes: EDITOR_NODES,
		onError: () => undefined
	});

const getSerializedNodeText = (node: SerializedLexicalNode): string => {
	if (isSerializedTextNode(node)) {
		return node.text;
	}

	if ('children' in node && Array.isArray(node.children)) {
		return node.children.map(getSerializedNodeText).join('');
	}

	return '';
};

const normalizeMarkdown = (markdown: string): string =>
	he.decode(markdown).replace(/\\([*_~`])/g, '$1');

const normalizeEscapedLineBreaks = (value: string): string => {
	if (!value || value.includes('\n') || value.includes('\r')) {
		return value;
	}

	return value
		.replace(/(?<!\\)\\r\\n/g, '\n')
		.replace(/(?<!\\)\\n/g, '\n')
		.replace(/(?<!\\)\\r/g, '\n');
};

const isSerializedTextNode = (
	node: SerializedLexicalNode
): node is SerializedTextNode =>
	node.type === 'text' &&
	typeof (node as Partial<SerializedTextNode>).text === 'string';

const isSerializedParagraphNode = (
	node: SerializedLexicalNode
): node is SerializedParagraphNode =>
	node.type === 'paragraph' &&
	'children' in node &&
	Array.isArray(node.children);

const isPlainMarkdownTextNode = (
	node: SerializedLexicalNode
): node is SerializedTextNode =>
	isSerializedTextNode(node) &&
	node.format === 0 &&
	node.detail === 0 &&
	node.mode === 'normal' &&
	node.style === '';

const isPlainMarkdownParagraphNode = (
	node: SerializedLexicalNode
): node is SerializedParagraphNode =>
	isSerializedParagraphNode(node) &&
	node.children.every(
		(child) => child.type === 'linebreak' || isPlainMarkdownTextNode(child)
	);

const isPlainMarkdownEditorState = (state: SerializedEditorState): boolean =>
	state.root.children.every(isPlainMarkdownParagraphNode);

const isParagraphOnlyEditorState = (state: SerializedEditorState): boolean =>
	state.root.children.every(isSerializedParagraphNode);

const plainMarkdownChildToText = (node: SerializedLexicalNode): string => {
	if (node.type === 'linebreak') {
		return '\n';
	}

	if (isSerializedTextNode(node)) {
		return node.text;
	}

	return '';
};

const getPlainMarkdownParagraphText = (node: SerializedParagraphNode): string =>
	normalizeEscapedLineBreaks(
		node.children.map(plainMarkdownChildToText).join('')
	);

const serializePlainMarkdownEditorState = (
	state: SerializedEditorState
): string =>
	state.root.children.reduce((markdown, node, index) => {
		if (!isSerializedParagraphNode(node)) {
			return markdown;
		}

		const paragraphText = getPlainMarkdownParagraphText(node);

		if (index === 0) {
			return paragraphText;
		}

		const previousNode = state.root.children[index - 1];
		const previousParagraphText = isSerializedParagraphNode(previousNode)
			? getPlainMarkdownParagraphText(previousNode)
			: '';
		const separator = previousParagraphText ? '\n\n' : '\n';

		return `${markdown}${separator}${paragraphText}`;
	}, '');

const serializeRichParagraphToMarkdown = (
	node: SerializedParagraphNode
): string => {
	if (
		!node.children.some((childNode) => getSerializedNodeText(childNode) !== '')
	) {
		return '';
	}

	const editor = createLexicalEditor();

	editor.update(
		() => {
			const root = $getRoot();
			root.clear();
			root.append($parseSerializedNode(node));
		},
		{ discrete: true }
	);

	let markdown = '';

	editor.getEditorState().read(() => {
		markdown = normalizeMarkdown($convertToMarkdownString(TRANSFORMERS));
	});

	return markdown;
};

const serializeParagraphNodeToMarkdown = (
	node: SerializedParagraphNode
): string => {
	if (isPlainMarkdownParagraphNode(node)) {
		return getPlainMarkdownParagraphText(node);
	}

	return serializeRichParagraphToMarkdown(node);
};

const serializeParagraphOnlyEditorState = (
	state: SerializedEditorState
): string => {
	let markdown = '';
	let previousParagraphText: string | null = null;

	for (const node of state.root.children) {
		if (!isSerializedParagraphNode(node)) {
			continue;
		}

		const paragraphText = serializeParagraphNodeToMarkdown(node);

		if (previousParagraphText === null) {
			markdown = paragraphText;
			previousParagraphText = paragraphText;
			continue;
		}

		const separator = previousParagraphText ? '\n\n' : '\n';
		markdown = `${markdown}${separator}${paragraphText}`;
		previousParagraphText = paragraphText;
	}

	return markdown;
};

const isSimplePlainTextEditorState = (
	state: SerializedEditorState
): boolean => {
	const rootChildren = state.root.children;

	if (rootChildren.length !== 1) {
		return false;
	}

	const [paragraphNode] = rootChildren;

	if (!isSerializedParagraphNode(paragraphNode)) {
		return false;
	}

	if (paragraphNode.children.length !== 1) {
		return false;
	}

	return isSerializedTextNode(paragraphNode.children[0]);
};

const ensureParagraph = () => {
	const root = $getRoot();
	if (root.getChildren().length === 0) {
		root.append($createParagraphNode().append($createTextNode('')));
	}
};

const createEmptyParagraphNode = () =>
	$createParagraphNode().append($createTextNode(''));

const appendEmptyParagraphs = (
	root: ReturnType<typeof $getRoot>,
	count: number
) => {
	for (let index = 0; index < count; index += 1) {
		root.append(createEmptyParagraphNode());
	}
};

const appendMarkdownBlockToRoot = (
	root: ReturnType<typeof $getRoot>,
	markdownBlock: string
) => {
	const blockEditor = createLexicalEditor();

	blockEditor.update(
		() => {
			const blockRoot = $getRoot();
			blockRoot.clear();

			try {
				$convertFromMarkdownString(markdownBlock, TRANSFORMERS);
			} catch {
				blockRoot.clear();
				blockRoot.append(
					$createParagraphNode().append($createTextNode(markdownBlock))
				);
			}

			ensureParagraph();
		},
		{ discrete: true }
	);

	const parsedBlock = blockEditor.getEditorState().toJSON();

	parsedBlock.root.children.forEach((childNode) => {
		root.append($parseSerializedNode(childNode));
	});
};

const applyDefaultMarkdownToRoot = (
	root: ReturnType<typeof $getRoot>,
	markdown: string
) => {
	try {
		$convertFromMarkdownString(markdown, TRANSFORMERS);
	} catch {
		root.clear();
		root.append($createParagraphNode().append($createTextNode(markdown)));
	}

	ensureParagraph();
};

const shouldPreserveBlankLineRuns = (markdown: string): boolean =>
	/\n{3,}/.test(markdown) || /\n\n+$/.test(markdown);

const applyMarkdownPreservingBlankLines = (
	root: ReturnType<typeof $getRoot>,
	markdown: string
) => {
	const lines = markdown.split('\n');
	let currentBlock: string[] = [];
	let blankLineCount = 0;
	let hasAppendedContent = false;

	const flushBlock = () => {
		if (currentBlock.length === 0) {
			return;
		}

		if (blankLineCount > 0) {
			appendEmptyParagraphs(
				root,
				hasAppendedContent ? Math.max(blankLineCount - 1, 0) : blankLineCount
			);
			blankLineCount = 0;
		}

		appendMarkdownBlockToRoot(root, currentBlock.join('\n'));
		currentBlock = [];
		hasAppendedContent = true;
	};

	for (const line of lines) {
		if (line.trim() === '') {
			flushBlock();
			blankLineCount += 1;
			continue;
		}

		currentBlock.push(line);
	}

	flushBlock();

	if (blankLineCount > 0) {
		appendEmptyParagraphs(root, blankLineCount);
	}

	ensureParagraph();
};

export const isSerializedEditorState = (
	state: unknown
): state is SerializedEditorState =>
	Boolean(
		state &&
			typeof state === 'object' &&
			'root' in state &&
			(state as { root?: unknown }).root
	);

export const getEmptyEditorState = (): SerializedEditorState =>
	plainTextToEditorState('');

export const normalizeEditorValue = (
	value?: SerializedEditorState | string | null
): SerializedEditorState => {
	if (isSerializedEditorState(value)) {
		return value;
	}

	if (typeof value === 'string') {
		return markdownToEditorState(value);
	}

	return getEmptyEditorState();
};

export const markdownToEditorState = (
	markdown: string
): SerializedEditorState => {
	if (typeof markdown !== 'string') {
		return getEmptyEditorState();
	}

	const normalizedInput = normalizeEscapedLineBreaks(markdown);

	const editor = createLexicalEditor();

	editor.update(
		() => {
			const root = $getRoot();
			root.clear();

			if (!normalizedInput) {
				ensureParagraph();
				return;
			}

			if (!shouldPreserveBlankLineRuns(normalizedInput)) {
				applyDefaultMarkdownToRoot(root, normalizedInput);
				return;
			}

			try {
				applyMarkdownPreservingBlankLines(root, normalizedInput);
			} catch {
				root.clear();
				applyDefaultMarkdownToRoot(root, normalizedInput);
			}
		},
		{ discrete: true }
	);

	return editor.getEditorState().toJSON();
};

export const plainTextToEditorState = (text: string): SerializedEditorState => {
	if (typeof text !== 'string') {
		return getEmptyEditorState();
	}

	const normalizedText = normalizeEscapedLineBreaks(text);

	const editor = createLexicalEditor();

	editor.update(
		() => {
			const root = $getRoot();
			root.clear();
			root.append(
				$createParagraphNode().append($createTextNode(normalizedText))
			);
			ensureParagraph();
		},
		{ discrete: true }
	);

	return editor.getEditorState().toJSON();
};

export const editorStateToMarkdown = (state: SerializedEditorState): string => {
	if (!isSerializedEditorState(state)) {
		return '';
	}

	try {
		if (
			isSimplePlainTextEditorState(state) ||
			isPlainMarkdownEditorState(state)
		) {
			return isSimplePlainTextEditorState(state)
				? normalizeEscapedLineBreaks(editorStateToPlainText(state))
				: serializePlainMarkdownEditorState(state);
		}

		if (isParagraphOnlyEditorState(state)) {
			return serializeParagraphOnlyEditorState(state);
		}

		const editor = createLexicalEditor();
		const editorState = editor.parseEditorState(JSON.stringify(state));
		editor.setEditorState(editorState);

		let markdown = '';

		editor.getEditorState().read(() => {
			markdown = normalizeMarkdown($convertToMarkdownString(TRANSFORMERS));
		});

		return markdown;
	} catch {
		return '';
	}
};

export const editorValueToMarkdown = (
	value?: SerializedEditorState | string | null
): string => {
	if (typeof value === 'string') {
		return value;
	}

	if (!value) {
		return '';
	}

	return editorStateToMarkdown(value);
};

export const normalizePlainMarkdownEditorState = (
	state: SerializedEditorState
): SerializedEditorState =>
	plainTextToEditorState(editorStateToMarkdown(state));

export const editorValueToPlainText = (
	value?: SerializedEditorState | string | null
): string => {
	if (typeof value === 'string') {
		return value;
	}

	if (!value) {
		return '';
	}

	return editorStateToPlainText(value);
};

export const editorStateToPlainText = (
	state: SerializedEditorState
): string => {
	if (!isSerializedEditorState(state)) {
		return '';
	}

	try {
		const editor = createLexicalEditor();
		const editorState = editor.parseEditorState(JSON.stringify(state));
		editor.setEditorState(editorState);

		let text = '';

		editor.getEditorState().read(() => {
			text = $getRoot().getTextContent();
		});

		return text;
	} catch {
		return '';
	}
};
